import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import type { MutateUserFBPermissions } from '@/utils/hooks/swr/useUserFBPermissions';

import useRouterQuery from '@/hooks/utils/useRouterQuery';

const DM_PERMISSION_PENDING_QUERY = 'dm_permission_pending';
const DM_PERMISSION_PENDING_ACTIVE = '1';
const PENDING_POLLING_INTERVAL_MS = 2_000;
const PENDING_TIMEOUT_MS = 30_000;

type UseDMPermissionPendingProps = {
    hasPermission: boolean;
    mutate: MutateUserFBPermissions;
};

export const useDMPermissionPending = ({ hasPermission, mutate }: UseDMPermissionPendingProps) => {
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();
    const [isPendingTimedOut, setIsPendingTimedOut] = useState(false);
    const isCleaningPendingQueryRef = useRef(false);

    const pendingQueryValue = router.query[DM_PERMISSION_PENDING_QUERY];
    const hasPendingQuery = Array.isArray(pendingQueryValue)
        ? pendingQueryValue.includes(DM_PERMISSION_PENDING_ACTIVE)
        : pendingQueryValue === DM_PERMISSION_PENDING_ACTIVE;

    const shouldPollPermissions = hasPendingQuery && !hasPermission && !isPendingTimedOut;

    const clearPendingQuery = useCallback(() => {
        if (!hasPendingQuery || isCleaningPendingQueryRef.current) {
            return;
        }

        isCleaningPendingQueryRef.current = true;
        router
            .replace(deleteQuery(DM_PERMISSION_PENDING_QUERY), undefined, { shallow: true })
            .catch(() => {
                isCleaningPendingQueryRef.current = false;
            });
    }, [deleteQuery, hasPendingQuery, router]);

    useEffect(() => {
        if (!hasPendingQuery) {
            isCleaningPendingQueryRef.current = false;
            setIsPendingTimedOut(false);
        }
    }, [hasPendingQuery]);

    useEffect(() => {
        if (!shouldPollPermissions) {
            return;
        }

        const interval = setInterval(() => {
            mutate();
        }, PENDING_POLLING_INTERVAL_MS);

        return () => {
            clearInterval(interval);
        };
    }, [mutate, shouldPollPermissions]);

    useEffect(() => {
        if (!shouldPollPermissions) {
            return;
        }

        const timeout = setTimeout(() => {
            setIsPendingTimedOut(true);
            clearPendingQuery();
        }, PENDING_TIMEOUT_MS);

        return () => {
            clearTimeout(timeout);
        };
    }, [clearPendingQuery, shouldPollPermissions]);

    useEffect(() => {
        if (!hasPermission || !hasPendingQuery) {
            return;
        }

        setIsPendingTimedOut(false);
        clearPendingQuery();
    }, [clearPendingQuery, hasPendingQuery, hasPermission]);

    const getOauthPendingRedirectQuery = useCallback(
        () => ({
            [DM_PERMISSION_PENDING_QUERY]: DM_PERMISSION_PENDING_ACTIVE
        }),
        []
    );

    return {
        shouldPollPermissions,
        getOauthPendingRedirectQuery
    };
};
